import React, { useEffect, useContext, useState, useCallback } from "react";
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from "@material-ui/core/styles";
import { AppContext, DispatchContext } from "../AppContext";
import Service from "../services/service";
import * as actions from "../actions/actions";
import { showCaptcha, formatFileSize, getCookie, createUUID, config, resolveNotificationMessage } from '../helpers/constants';
import { getCsrfToken } from '../helpers/api';
import { LinearProgress, Box, Paper, Typography, Button } from "@material-ui/core";
import FullScreenPage from "./FullScreenPage";
import LicenseQuotePanel from './LicenseQuotePanel';
import DividerLabel from "./DividerLabel";
import { ReactComponent as CheckedIcon } from '../assets/icons/checked.svg';
import LoadingScreen from "./LoadingScreen";
import FileUploadCards from "./FileUploadCards";
import { ReactComponent as UploadingIcon } from '../assets/icons/uploading.svg';
import IconLabel from "./IconLabel";
import { initialState } from "../reducer/InitialState";




const useStyles = makeStyles((theme) => ({
  paper: {
    width: 850,
    minHeight: 600,
    position: "relative",
    boxShadow: "0px 0px 5px 5px rgba(0, 0, 0, 0.25)",
    "&:hover": {
      boxShadow: "0px 0px 7px 7px rgba(0, 0, 0, 0.4)",
      transition: "box-shadow 0.3s ease-in-out",
    }
  },
  drag_drop_container: {
    overflowX: "hidden",
    overflowY: "auto",
    flexDirection: "column",
    display: "flex",
    height: 320,
    padding: 8,
    cursor: "pointer",
    '&:focus': {
      outline: 'none'
    }
  }
}));


let uploadedFiles = [];
let xhrs = [];
let uploadStatusTimer = 0;

export default function WizardPanel(props) {
  const dispatch = useContext(DispatchContext);
  const state = useContext(AppContext);
  const s = state.strings;
  const wz = state.checkout.wizard;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);



  useEffect(() => {
    return () => {
      xhrs.map((x) => x.abort());
      xhrs = [];
      uploadedFiles = [];
      clearInterval(uploadStatusTimer);
    };
  }, []);


  const onDrop = (droppedFiles) => {
    let maxAllowedCount = config.maxNumberFileUploadForm - uploadedFiles.length;
    if (droppedFiles.length > maxAllowedCount) {
      let skippedCount = droppedFiles.length - maxAllowedCount;
      dispatch(actions.openToast({ type: "warning", message: s.newCheck.filesTruncated.format(config.maxNumberFileUploadForm, skippedCount), status: true }));
      droppedFiles = droppedFiles.slice(0, maxAllowedCount);
    }

    droppedFiles.map(file => {
      const uuid = createUUID();
      file.id = uuid;
    })

    dispatch(actions.setUploadedFiles(droppedFiles));

    droppedFiles.map((file, i) => {
      const uuid = file.id;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadid', uuid);
      if (document.getElementById(uuid)) {
        ReactDOM.render(<LinearProgress variant="determinate" value={0} />, document.getElementById(uuid))
      }
      let xhr = new XMLHttpRequest();
      xhrs.push(xhr);

      xhr.upload.onprogress = (e) => {
        const pct = +((e.loaded / e.total) * 100).toFixed(2);
        if (document.getElementById(uuid + '_status')) {
          ReactDOM.render(`${formatFileSize(e.loaded)} / ${formatFileSize(e.total)} (${pct})%`, document.getElementById(uuid + '_status'));
        }
        if (document.getElementById(uuid)) {
          ReactDOM.render(pct !== 100 ? <LinearProgress variant="determinate" value={pct} /> : <LinearProgress value={pct} />, document.getElementById(uuid))
        }
      };
      uploadedFiles.push(uuid)
      xhr.onreadystatechange = () => {

        if (xhr.readyState !== 4) return;   // 4: DONE
        if (xhr.status !== 200) {
          return;
        }
        const response = JSON.parse(xhr.response)
        if (response && !response.success) {
          onUploadedDeleteFile(uuid);
          dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(response.errors[0], s.notificationMessage), status: true }))
        }
        /*handle success*/
      };

      xhr.open("post", config.backendUrl + "api/UploadFile");
      const token = getCsrfToken();
      if (token) xhr.setRequestHeader('X-CSRF-Token', token);
      const sid = getCookie(config.sessionCookieName);
      if (sid) xhr.setRequestHeader('X-Session-Id', sid);
      xhr.send(formData);
    });

    getUploadedFilesStatus(uploadedFiles)

  }

  const onUploadedDeleteFile = (id) => {
    // todo: abort xhr for this file
    uploadedFiles = uploadedFiles.filter(file => file !== id);
    if (uploadedFiles.length > 0) {
      getUploadedFilesStatus(uploadedFiles)
    }
    else {
      onCancelAllFilesUpload();
    }
    dispatch(actions.removeUploadedFile(id));
  }

  const getUploadedFilesStatus = (uploadids) => {

    clearInterval(uploadStatusTimer);

    uploadStatusTimer = setInterval(() => {
      Service.getUploadedFilesStatus(uploadids).then(res => {

        res.data.Files && res.data.Files.filter(f => f.Status == "Error").map(f => {
          onUploadedDeleteFile(f.UploadId);
          dispatch(actions.openToast({ type: "error", message: s.newCheck.uploadError, status: true }))
        });

        if (res.data.PendingCount === 0 && res.total !== 0 && uploadids.length === res.total) {
          clearInterval(uploadStatusTimer);
        }
        dispatch(actions.setUploadedFilesStatus(res.data))
        // check if there are files in status=Error and delete them from the list

      })
    }, 2500);
  }

  const onCancelAllFilesUpload = () => {
    xhrs.map((x) => x.abort());
    xhrs = [];
    uploadedFiles = [];
    clearInterval(uploadStatusTimer);
    dispatch(actions.cancelFilesUpload());
    dispatch(actions.setUploadedFilesStatus({ Files: [] }))
    dispatch(actions.setCheckoutDetails({ wizard: initialState.checkout.wizard, productId: null }));

  }


  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });


  return (
    <>
      <div className={classes.drag_drop_container} {...getRootProps()}>
        <input {...getInputProps()} />
        {state.uploadedFiles.length > 0 ?
          <>
            <Box mt={6} />
            <FileUploadCards singleFile acceptedFiles={state.uploadedFiles} fileStatus={state.uploadedFilesStatus} onUploadedDeleteFile={onUploadedDeleteFile} isDragActive={isDragActive} />
            {wz.requiredCredits > 0 &&
              <div style={{ marginTop:24, width: "100%", display: "flex", justifyContent: "center" }}>
                {state.user.loggedIn && !state.user.guest ?
                  <IconLabel type="check" centered mt={24} spacing={80} label={s.wizard.requiredCreditsLoggedIn.format(wz.requiredCredits, state.user.credits)} /> :
                  <IconLabel type="check" centered mt={24} spacing={80} label={s.wizard.requiredCredits.format(wz.requiredCredits)} />}
              </div>
            }
          </> :
          isDragActive ?
            <div style={{ borderRadius: 5, border: "2px solid #0D4A81", backgroundColor: "#eeeeee", width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <UploadingIcon fill="#1982DD" height={100} width={100} style={{ marginBottom: 16 }} />
              <Typography align="center">{s.newCheck.dropHere}</Typography>
            </div> :
            <div style={{ borderRadius: 5, border: "2px dotted #0D4A81", width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <UploadingIcon fill="#0D4A81" height={100} width={100} style={{ marginBottom: 16 }} />
              <Typography paragraph align="center">{s.dragYourFileHere}</Typography>
              <Typography variant="body2" color="secondary" align="center">{s.newCheck.parallelUpload.format(config.maxNumberFileUploadForm)}</Typography>
              <Typography variant="body2" color="secondary" align="center">{s.newCheck.supportFileFormats}</Typography>
              <Typography variant="body2" color="secondary" align="center">{s.newCheck.maxFileSize}</Typography>
            </div>
        }
      </div>
    </>
  );

}
